import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Users, CreditCard, Calendar, BarChart3, UserPlus, LogIn, LogOut, Clock, DollarSign, Settings, Search, Plus, Edit, Trash2, Eye, EyeOff, Printer, CheckCircle, XCircle, X } from "lucide-react";
import { offlineManager, useOfflineStatus } from "../lib/offlineManager";
import { API, apiCall } from "../lib/api";
import { secureStorage } from "../lib/secureStorage";
import { rememberCredentials, verifyOffline, hasAnyCreds } from "../lib/offlineAuth";
import { useAuth } from "../context/AuthContext";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { getPaymentMethodLabel, getPaymentStatusLabel, getSubscriptionPaymentStatusLabel, generateReceiptPDF } from "../lib/receipts";
import { LanguageSelector } from "../components/LanguageSelector";

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotUsername, setForgotUsername] = useState('admin');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPwd, setForgotNewPwd] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showForgotPwd, setShowForgotPwd] = useState(false);

  // 2FA (Phase 2)
  const [twoFA, setTwoFA] = useState(null); // {challenge_id, methods, email_masked, code_sent}
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('email');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [pendingCreds, setPendingCreds] = useState(null);

  const cancel2FA = () => { setTwoFA(null); setTwoFACode(''); setPendingCreds(null); };

  const requestEmailCode = async () => {
    try {
      const r = await apiCall('/auth/login/request-email-code', { method: 'POST', data: { challenge_id: twoFA.challenge_id } });
      toast.success('Code envoyé à ' + (r.email_masked || 'votre e-mail'));
      setTwoFAMethod('email');
    } catch (e) {
      toast.error("Impossible d'envoyer le code par e-mail.");
    }
  };

  const submit2FA = async () => {
    setTwoFALoading(true);
    try {
      const resp = await apiCall('/auth/login/verify-2fa', {
        method: 'POST',
        data: { challenge_id: twoFA.challenge_id, code: twoFACode.trim(), method: twoFAMethod, remember_device: rememberDevice },
      });
      if (!resp || !resp.access_token) throw new Error('NO_TOKEN');
      if (resp.device_token) secureStorage.setDeviceToken(resp.device_token);
      login(resp.user, resp.access_token, resp.refresh_token);
      if (pendingCreds) await rememberCredentials(pendingCreds.u, pendingCreds.p, resp.user);
      offlineManager.triggerSync().catch(() => {});
      toast.success('Connexion réussie !');
      cancel2FA();
    } catch (error) {
      toast.error(error?.detail || 'Code de vérification incorrect ou expiré.');
    } finally {
      setTwoFALoading(false);
    }
  };


  const resetForgotState = () => {
    setForgotStep(1); setForgotUsername('admin'); setForgotCode(''); setForgotNewPwd(''); setForgotLoading(false); setShowForgotPwd(false);
  };

  const sendForgotCode = async () => {
    setForgotLoading(true);
    try {
      await apiCall('/auth/forgot-password', { method: 'POST', data: { username: forgotUsername.trim() } });
      toast.success("Si le compte existe, un code a été envoyé à l'e-mail de secours de la salle.");
      setForgotStep(2);
    } catch (e) {
      toast.error("Impossible d'envoyer le code. Vérifiez votre connexion.");
    } finally {
      setForgotLoading(false);
    }
  };

  const confirmForgotReset = async () => {
    if (forgotNewPwd.trim().length < 8) { toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères'); return; }
    setForgotLoading(true);
    try {
      await apiCall('/auth/reset-password-confirm', { method: 'POST', data: { username: forgotUsername.trim(), code: forgotCode.trim(), new_password: forgotNewPwd.trim() } });
      toast.success('Mot de passe réinitialisé. Vous pouvez vous connecter.');
      setShowForgot(false);
      resetForgotState();
    } catch (e) {
      toast.error(e?.detail || 'Code invalide ou expiré.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const u = username.trim();
    const p = password.trim();

    try {
      const response = await apiCall('/auth/login', {
        method: 'POST',
        data: { username: u, password: p, device_token: secureStorage.getDeviceToken() }
      });

      // Étape 2FA requise : afficher l'écran de vérification (pas de connexion immédiate)
      if (response && response.two_factor_required) {
        setPendingCreds({ u, p });
        const methods = response.methods || {};
        setTwoFAMethod(methods.totp ? 'totp' : 'email');
        setTwoFACode('');
        setRememberDevice(false);
        setTwoFA({
          challenge_id: response.challenge_id,
          methods,
          email_masked: response.email_masked || '',
          code_sent: !!response.code_sent,
        });
        setLoading(false);
        return;
      }

      if (!response || !response.access_token) {
        throw new Error('NO_TOKEN');
      }

      login(response.user, response.access_token, response.refresh_token);
      // Mémoriser le compte pour permettre la reconnexion hors-ligne ultérieure
      await rememberCredentials(u, p, response.user);
      // Envoyer immédiatement tout ce qui a été saisi hors-ligne
      offlineManager.triggerSync().catch(() => {});
      toast.success('Connexion réussie !');
    } catch (error) {
      const msg = String(error?.message || '');
      const status = error?.status;
      const isNetworkError =
        !navigator.onLine ||
        msg.includes('Failed to fetch') ||
        msg.includes('NetworkError') ||
        msg.includes('Load failed') ||
        msg === 'NO_TOKEN';

      // Identifiants explicitement refusés par le serveur (en ligne) : ne pas tenter le hors-ligne
      if (status === 429 || msg.includes('429')) {
        toast.error(error?.detail || 'Trop de tentatives de connexion. Réessayez dans quelques minutes.');
      } else if (status === 401 || (msg.includes('401') && !isNetworkError)) {
        toast.error('Nom d\'utilisateur ou mot de passe incorrect.');
      } else if (status === 403 || msg.includes('403')) {
        toast.error('Ce compte est désactivé. Contactez un administrateur.');
      } else if (isNetworkError) {
        // Pas de réseau : tenter une connexion HORS-LIGNE
        const off = await verifyOffline(u, p);
        if (off && off.user) {
          login(off.user, off.token || 'offline');
          toast.success('Connecté en mode hors-ligne. Vos saisies seront synchronisées au retour du réseau.');
        } else if (hasAnyCreds()) {
          toast.error('Identifiants incorrects (mode hors-ligne).');
        } else {
          toast.error('Connexion hors-ligne indisponible : connectez-vous d\'abord une fois en ligne sur cet appareil.');
        }
      } else {
        toast.error('Impossible de contacter le serveur. Vérifiez votre connexion internet puis réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <div className="fixed right-4 top-4 z-10" data-testid="login-language-selector"><LanguageSelector /></div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-orange-800">Racinefit</CardTitle>
          <CardDescription>Système de gestion de salle de sport</CardDescription>
        </CardHeader>
        <CardContent>
          {twoFA ? (
          <div className="space-y-4" data-testid="twofa-step">
            <div className="text-center">
              <p className="text-sm text-gray-600">Vérification en deux étapes requise pour ce compte.</p>
            </div>

            {twoFA.methods?.totp && twoFA.methods?.email && (
              <div className="flex gap-2 justify-center">
                <Button type="button" size="sm" variant={twoFAMethod === 'totp' ? 'default' : 'outline'}
                  className={twoFAMethod === 'totp' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={() => setTwoFAMethod('totp')} data-testid="twofa-method-totp">Application</Button>
                <Button type="button" size="sm" variant={twoFAMethod === 'email' ? 'default' : 'outline'}
                  className={twoFAMethod === 'email' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={requestEmailCode} data-testid="twofa-method-email">Code par e-mail</Button>
              </div>
            )}

            <p className="text-xs text-center text-gray-500" data-testid="twofa-hint">
              {twoFAMethod === 'totp'
                ? "Saisissez le code à 6 chiffres de votre application d'authentification."
                : twoFAMethod === 'recovery'
                ? "Saisissez l'un de vos codes de secours (usage unique)."
                : `Code par e-mail : un code a été envoyé à ${twoFA.email_masked || 'votre e-mail'}.`}
            </p>

            <div className="space-y-2">
              <Label htmlFor="twofa-code">{twoFAMethod === 'recovery' ? 'Code de secours' : 'Code de vérification'}</Label>
              <Input id="twofa-code"
                inputMode={twoFAMethod === 'recovery' ? 'text' : 'numeric'}
                maxLength={twoFAMethod === 'recovery' ? 9 : 6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(twoFAMethod === 'recovery'
                  ? e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()
                  : e.target.value.replace(/\D/g, ''))}
                placeholder={twoFAMethod === 'recovery' ? 'xxxx-xxxx' : '123456'} autoFocus data-testid="twofa-code-input" />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} data-testid="twofa-remember-device" />
              Faire confiance à cet appareil pendant 30 jours
            </label>

            <Button type="button" className="w-full bg-orange-600 hover:bg-orange-700"
              onClick={submit2FA}
              disabled={twoFALoading || (twoFAMethod === 'recovery' ? twoFACode.replace(/[^a-z0-9]/g, '').length < 8 : twoFACode.length !== 6)}
              data-testid="twofa-submit-button">
              {twoFALoading ? 'Vérification...' : 'Vérifier'}
            </Button>

            {twoFAMethod === 'totp' && twoFA.methods?.email && (
              <button type="button" onClick={requestEmailCode} className="text-sm text-orange-700 hover:underline w-full text-center" data-testid="twofa-send-email-link">
                Recevoir plutôt un code par e-mail
              </button>
            )}

            {twoFAMethod !== 'recovery' ? (
              <button type="button" onClick={() => { setTwoFAMethod('recovery'); setTwoFACode(''); }} className="text-sm text-orange-700 hover:underline w-full text-center" data-testid="twofa-use-recovery-link">
                Utiliser un code de secours
              </button>
            ) : (
              <button type="button" onClick={() => { setTwoFAMethod(twoFA.methods?.totp ? 'totp' : 'email'); setTwoFACode(''); }} className="text-sm text-gray-500 hover:underline w-full text-center" data-testid="twofa-back-method-link">
                ← Revenir au code standard
              </button>
            )}

            <button type="button" onClick={cancel2FA} className="text-sm text-gray-500 hover:underline w-full text-center" data-testid="twofa-cancel">
              ← Annuler
            </button>
          </div>
          ) : (
          <>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Nom d'utilisateur</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                spellCheck={false}
                required
                data-testid="username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="current-password"
                  spellCheck={false}
                  className="pr-10"
                  required
                data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={loading || !username.trim() || !password} data-testid="login-submit-button">
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => { resetForgotState(); setShowForgot(true); }}
              className="text-sm text-orange-700 hover:underline"
              data-testid="forgot-password-link"
            >
              Mot de passe administrateur oublié ?
            </button>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForgot} onOpenChange={(o) => { setShowForgot(o); if (!o) resetForgotState(); }}>
        <DialogContent className="sm:max-w-[425px]" data-testid="forgot-password-dialog" closeTestId="forgot-password-dialog-close">
          <DialogHeader>
            <DialogTitle>Récupération administrateur</DialogTitle>
            <DialogDescription>
              {forgotStep === 1
                ? "Un code de récupération sera envoyé à l'e-mail de secours de la salle (réservé au compte administrateur principal)."
                : "Saisissez le code reçu par e-mail puis choisissez un nouveau mot de passe."}
            </DialogDescription>
          </DialogHeader>
          {forgotStep === 1 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-username">Nom d'utilisateur administrateur</Label>
                <Input id="forgot-username" value={forgotUsername} onChange={(e) => setForgotUsername(e.target.value)} autoCapitalize="none" spellCheck={false} data-testid="forgot-username-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowForgot(false)} data-testid="forgot-cancel-button">Annuler</Button>
                <Button className="bg-orange-600 hover:bg-orange-700" onClick={sendForgotCode} disabled={forgotLoading || !forgotUsername.trim()} data-testid="forgot-send-code-button">
                  {forgotLoading ? 'Envoi...' : 'Envoyer le code'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-code">Code de récupération (6 chiffres)</Label>
                <Input id="forgot-code" inputMode="numeric" maxLength={6} value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" data-testid="forgot-code-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="forgot-new-pwd">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input id="forgot-new-pwd" type={showForgotPwd ? 'text' : 'password'} value={forgotNewPwd} onChange={(e) => setForgotNewPwd(e.target.value)} placeholder="Au moins 8 caractères" className="pr-10" data-testid="forgot-new-password-input" />
                  <button type="button" onClick={() => setShowForgotPwd((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700" aria-label={showForgotPwd ? "Masquer le nouveau mot de passe" : "Afficher le nouveau mot de passe"} data-testid="toggle-forgot-password-visibility">
                    {showForgotPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-between gap-2">
                <Button variant="ghost" size="sm" onClick={() => setForgotStep(1)} data-testid="forgot-resend-code-button">← Renvoyer un code</Button>
                <Button className="bg-orange-600 hover:bg-orange-700" onClick={confirmForgotReset} disabled={forgotLoading || forgotCode.length !== 6 || forgotNewPwd.trim().length < 8} data-testid="forgot-confirm-button">
                  {forgotLoading ? 'Validation...' : 'Réinitialiser'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard Component

export default Login;
